import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import styled, { useTheme } from 'styled-components'
import firstBy from 'thenby'
import moment from 'moment'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip
} from 'chart.js'
import 'chartjs-adapter-moment'
import { Line } from 'react-chartjs-2'
import createCustomTooltip from './createCustomTooltip'
import { P2, Caption4 } from '../../styled/typography/typography'
import { SKILL_CHECK_MAX_RATING } from '../../../constants'
import { getWorkSkillTier, getTierNumber } from 'utils/workskills'
import { getMasteryLevel, getMasteryLevelNumber } from 'utils/skills'
import MasteryLevel from '../../mini/Skills/MasteryLevel'
import withApiData from '../../HOC/withApiData'
import useLexicon from '../../../hooks/useLexicon'
import colors from '../../../configuration/colors'

ChartJS.register(
  CategoryScale,
  LinearScale,
  TimeScale,
  PointElement,
  LineElement,
  Tooltip
)

const Wrapper = styled.div`
  display: flex;
  align-items: stretch;
`

const ChartJsWrapper = styled.div`
  flex: 1;
  height: 300px;
  width: 600px;
  position: relative;
  margin: -34px -8px 0;
`

const YAxis = styled.div`
  display: grid;
  grid-auto-rows: 1fr;
  grid-auto-flow: row;
  justify-items: center;
  align-items: center;
  padding-bottom: ${props => (props.chartIsEmpty ? '15px' : '30px')};
`

const Caption4Regular = styled(Caption4)`
  font-weight: 400;
  text-align: center;
`

const NoData = styled(P2)`
  position: absolute;
  top: 53%;
  left: 50%;
  padding: 10px 20px;
  background: ${props => props.theme.cardBackground};
  transform: translate(-50%, -50%);
`

const Chart = ({
  apiData,
  dispatch,
  responses,
  mastery,
  isMentor,
  student,
  skill,
  isWorkSkill,
  autoOpenUcqId
}) => {
  const lexicon = useLexicon()
  const theme = useTheme()
  const [chart, setChart] = useState(null)

  const maxRating = isWorkSkill ? skill.maximum_value : SKILL_CHECK_MAX_RATING

  const CustomTooltip = useMemo(
    () => createCustomTooltip(apiData, student, isMentor, lexicon, theme),
    [apiData, student, isMentor]
  )

  const getFilteredResponses = () => {
    let data = []

    if (responses.student.length > 0) {
      data = data.concat(responses.student)
    }

    if (responses.mentor.length > 0) {
      data = data.concat(responses.mentor)
    }

    return data
  }

  const getDateRange = () => {
    const sortedResponses = getFilteredResponses().sort(firstBy('assessed_at'))
    return [
      sortedResponses.length > 0
        ? moment.unix(sortedResponses[0].assessed_at).add(-1, 'days')
        : moment(),
      sortedResponses.length > 0
        ? moment
            .unix(sortedResponses[sortedResponses.length - 1].assessed_at)
            .add(1, 'days')
        : moment()
    ]
  }

  const getData = () => {
    const studentDataset = {
      data: responses.student.sort(firstBy('assessed_at')).map(r => {
        return {
          x: moment.unix(r.assessed_at),
          y: parseFloat(r.rating),
          id: r.id,
          color: '#90B1C3',
          masteryLevel: isWorkSkill
            ? getWorkSkillTier(parseFloat(r.rating), skill)
            : getMasteryLevel(parseFloat(r.rating)),
          masteryLevelDescription: getMasteryLevelDescription(
            parseFloat(r.rating)
          ),
          dispatch,
          student,
          skill,
          isWorkSkill
        }
      }),
      label: skill.name + ' - student',
      clip: false,
      borderColor: '#90B1C3',
      fill: false,
      steppedLine: false,
      pointStyle: 'rectRot',
      pointBorderColor: '#90B1C3',
      pointRadius: 7,
      pointBorderWidth: 3,
      pointBackgroundColor: '#fff',
      pointHoverRadius: 7,
      pointHoverBorderWidth: 3,
      pointHoverBackgroundColor: '#90B1C3',
      pointHoverBorderColor: '#90B1C3'
    }

    // TODO: refactor this and above into a parameterized (isMentor) function
    const mentorDataset = {
      data: responses.mentor.sort(firstBy('assessed_at')).map(r => {
        return {
          x: moment.unix(r.assessed_at),
          y: parseFloat(r.rating),
          id: r.id,
          color: '#4E6C84',
          masteryLevel: isWorkSkill
            ? getWorkSkillTier(parseFloat(r.rating), skill)
            : getMasteryLevel(parseFloat(r.rating)),
          masteryLevelDescription: getMasteryLevelDescription(
            parseFloat(r.rating)
          ),
          dispatch,
          isMentor,
          student,
          skill,
          isWorkSkill,
          userCompiledQuestId: r.user_compiled_quest_id
        }
      }),
      label: skill.name + ' - mentor',
      clip: false,
      borderColor: '#4E6C84',
      fill: false,
      steppedLine: false,
      pointStyle: 'rect',
      pointBorderColor: '#4E6C84',
      pointRadius: 7,
      pointBorderWidth: 3,
      pointBackgroundColor: '#fff',
      pointHoverRadius: 7,
      pointHoverBorderWidth: 3,
      pointHoverBackgroundColor: '#4E6C84',
      pointHoverBorderColor: '#4E6C84'
    }

    const datasets = [studentDataset, mentorDataset]

    // paint mastery range
    if (mastery !== null) {
      datasets.push({
        label: 'mastery',
        data: getDateRange().map(endpoint => ({ x: endpoint, y: mastery })),
        clip: 0,
        fill: false,
        borderColor: [
          isWorkSkill ? 'rgba(36,181,126,0.16)' : 'rgba(79,158,233,0.2)'
        ],
        borderWidth: 44,
        pointRadius: 0
      })
    }

    const labels = getFilteredResponses()
      .sort(firstBy('assessed_at'))
      .map(r => moment.unix(r.assessed_at).format('D MMM'))

    return {
      labels: [...new Set(labels)],
      datasets
    }
  }

  const getDataColor = (color, isTranslucent) => {
    return isTranslucent ? color.replace(', 1)', ', 0.5)') : color
  }

  const getMasteryLevelDescription = rating => {
    if (rating <= 1) {
      return `Has not demonstrated evidence of this ${lexicon.skill.toLowerCase()}.`
    } else if (rating <= 2) {
      return `Expresses familiarity with the ${lexicon.skill.toLowerCase()}, shows awareness of the ${lexicon.skill.toLowerCase()} and attempts to demonstrate it.`
    } else if (rating <= 3) {
      return `Demonstrates the ${lexicon.skill.toLowerCase()} in the intended manner, with evidence that the ${lexicon.skill.toLowerCase()} has been acquired.`
    } else {
      return `Demonstrates the ${lexicon.skill.toLowerCase()} in multiple contexts, applies the ${lexicon.skill.toLowerCase()} to new uses.`
    }
  }

  const hideTooltip = () => {
    const ttEl = document.getElementById('chartjs-tooltip')
    if (ttEl && ttEl.parentNode) {
      ttEl.parentNode.removeChild(ttEl)
    }
  }

  const getOptions = () => {
    const [minDate, maxDate] = getDateRange()
    const diff = maxDate.diff(minDate, 'days')

    return {
      events: ['click', 'mouseout'],
      layout: {
        padding: {
          top: 35,
          left: 20,
          right: 20
        }
      },
      maintainAspectRatio: false,
      animation: {
        // faster animation so we don't have to wait too long for the tooltip
        duration: autoOpenUcqId ? 500 : 1000,
        animateScale: true,
        animateRotate: false
      },
      elements: {
        line: {
          tension: 0 // disables bezier curves
        }
      },
      scales: {
        y: {
          min: 0,
          max: maxRating,
          ticks: {
            stepSize: 1,
            display: false
          },
          grid: {
            drawTicks: false,
            display: false,
            borderColor: theme?.chartGridLines
          }
        },
        yGridlines: {
          min: 0.5,
          max: maxRating - 0.5,
          ticks: {
            stepSize: 1,
            display: false
          },
          offset: true,
          grid: {
            drawTicks: false,
            drawBorder: false,
            color: theme?.chartGridLines
          }
        },
        x: {
          type: 'time',
          min: minDate,
          max: maxDate,
          time: {
            unit: getXAxesUnit(diff),
            displayFormats: {
              day: 'D MMM',
              week: 'D MMM',
              month: 'D MMM'
            }
          },
          grid: {
            display: false,
            borderColor: theme?.chartGridLines
          },
          ticks: {
            fontFamily: "'Lato', sans-serif",
            fontStyle: '300',
            minRotation: 0,
            maxRotation: 0,
            color: theme?.h4
          }
        }
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: CustomTooltip
        }
      }
    }
  }

  const getXAxesUnit = numOfDays => {
    if (numOfDays === 0) {
      return 'hour'
    } else if (numOfDays <= 7) {
      return 'day'
    } else if (numOfDays <= 60) {
      return 'week'
    } else {
      return 'month'
    }
  }

  const data = getData()

  // https://stackoverflow.com/questions/39283177/programmatically-open-and-close-chart-js-tooltip
  const openTooltip = (datasetIndex, dataIndex) => {
    if (!chart || !chart.canvas) return // in case we unmount right before executing this

    const chartMeta = chart.getDatasetMeta(datasetIndex)
    const canvasRect = chart.canvas.getBoundingClientRect()
    const point = chartMeta.data[dataIndex].getCenterPoint()

    const clickEvent = new MouseEvent('click', {
      clientX: canvasRect.left + point.x,
      clientY: canvasRect.top + point.y
    })

    chart.canvas.dispatchEvent(clickEvent)
  }

  useEffect(() => {
    if (autoOpenUcqId && !!chart) {
      // find the checks from the specified quest, pick the most recent one
      const toOpen = data.datasets[1].data
        .map((point, index) => ({
          index,
          ucqId: point.userCompiledQuestId
        }))
        .reverse()
        .find(point => point.ucqId === autoOpenUcqId)

      if (toOpen) {
        // auto open its tooltip
        setTimeout(() => {
          openTooltip(1, toOpen.index)
        }, 500) // wait for the animation to finish

        return hideTooltip
      }
    }
  }, [chart, autoOpenUcqId])

  useEffect(() => {
    if (autoOpenUcqId && !!chart?.chartInstance) {
      // find the checks from the specified quest, pick the most recent one
      const toOpen = data.datasets[1].data
        .map((point, index) => ({
          index,
          ucqId: point.userCompiledQuestId
        }))
        .reverse()
        .find(point => point.ucqId === autoOpenUcqId)

      if (toOpen) {
        // auto open its tooltip
        setTimeout(() => {
          openTooltip(1, toOpen.index)
        }, 500) // wait for the animation to finish

        return hideTooltip
      }
    }
  }, [chart, autoOpenUcqId])

  useEffect(() => {
    return hideTooltip
  }, [])

  return (
    <Wrapper>
      <YAxis chartIsEmpty={data.labels.length === 0}>
        {Array(maxRating)
          .fill(0)
          .map((_, i) =>
            mastery !== null &&
            (isWorkSkill
              ? getTierNumber(mastery, true)
              : getMasteryLevelNumber(mastery, true)) ===
              maxRating - i - 1 ? (
              <MasteryLevel
                masteryRating={mastery}
                workSkill={isWorkSkill && skill}
                needsAggregateCutoffAdjustment
              />
            ) : (
              <Caption4Regular victoria>
                {isWorkSkill
                  ? getWorkSkillTier(maxRating - i - 0.5, skill)
                  : getMasteryLevel(maxRating - i - 0.5)}
              </Caption4Regular>
            )
          )}
      </YAxis>

      <ChartJsWrapper>
        {data.labels.length === 0 && <NoData>Nothing yet.</NoData>}

        <Line
          width={740}
          height={300}
          options={getOptions()}
          legend={{ display: false }}
          data={data}
          ref={setChart}
        />
      </ChartJsWrapper>
    </Wrapper>
  )
}

Chart.propTypes = {
  dispatch: PropTypes.func,
  student: PropTypes.object,
  skill: PropTypes.object,
  isWorkSkill: PropTypes.bool,
  isMentor: PropTypes.bool,
  responses: PropTypes.array,
  mastery: PropTypes.number,
  autoOpenUcqId: PropTypes.number
}

export default withApiData(Chart)
